import { FormSelect } from "@/components/FormSelect";
import { HelpTooltip } from "./HelpTooltip";
// Owners declare which reads each action affects. Shared controls only execute and present status.
import { invalidateReads, type ReadTarget } from "@/lib/query-invalidation";
import { useQueryClient } from "@tanstack/react-query";
import { useId, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { Dialog } from "./overlays";

type Tone = "accent" | "plain" | "ghost" | "danger" | "ok";
/** Named size slots (UI philosophy action table): `default` is the 32px/13px workspace action; `row` is the 26px/12px
 *  compact action of a dense card or row (lineage graph cards), where every peer in that row uses the same slot. */
export type BtnSize = "default" | "row" | "form" | "icon" | "menu";
// One action shape. Semantic tints stay quiet beside the primary action.
const SHAPE = "celia-button inline-flex items-center disabled:opacity-50";
// Controls share one scale with Select and native fields: 32px/13px in panels and toolbars, 36px/14px to submit a form.
const SIZE: Record<BtnSize, string> = { default: "h-8 px-3.5 text-[13px]", row: "h-[26px] gap-[5px] px-2.5 text-xs", form: "h-9 px-4 text-sm", icon: "celia-button--icon h-8 w-8 justify-center p-0", menu: "celia-button--menu w-full min-h-8 px-2.5 text-[13px]" };
const BTN = `${SHAPE} ${SIZE.default}`;
const TONE: Record<Tone, string> = {
  accent: "celia-button--accent",
  plain: "celia-button--plain border",
  ghost: "celia-button--ghost",
  danger: "celia-button--danger border",
  ok: "celia-button--ok",
};

/** The one button skin. A feature names a tone and, where its slot needs one, a named size; it never writes the classes. */
export function btn(tone: Tone = "accent", size: BtnSize = "default"): string {
  return `${SHAPE} ${SIZE[size]} ${TONE[tone]}`;
}

/** Repeated card navigation/preview is quiet; page and form actions use btn(). */
export function cardAction(): string {
  return `${btn("ghost", "row")} celia-card-action`;
}

type Msg = { ok: boolean; text: string; reauth?: boolean };

function useRun(reads: readonly ReadTarget[]) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg | null>(null);
  async function run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setBusy(true);
    setMsg(null);
    try {
      const out = await fn();
      setMsg({ ok: true, text: "done" });
      await invalidateReads(qc, reads);
      return out;
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message, reauth: ["reauthentication_required", "mfa_required"].includes((e as { code?: string }).code ?? "") });
      return undefined;
    } finally {
      setBusy(false);
    }
  }
  return { busy, msg, run };
}

/** An administrative step needs a recent sign-in; the link returns to this page. */
function ReauthLink() {
  return <a className="font-medium text-accent-ink" href={`/api/auth/reauthenticate?back=${encodeURIComponent(location.pathname + location.search)}`}>Sign in again and verify your identity</a>;
}

function Note({ msg }: { msg: Msg | null }) {
  return msg ? <span className={`text-xs ${msg.ok ? "text-second" : "text-danger"}`}>{msg.text}{msg.reauth && <> <ReauthLink /></>}</span> : null;
}

/** The label above a field: the form's only text, so it stays quiet. */
function FieldLabel({ children }: { children: ReactNode }) {
  return <span className="text-xs font-medium text-muted">{children}</span>;
}

/** A button for one call; consequential decisions and reasons stay in the same native dialog. */
export function Action({ reads, call, label, confirm, ask, tone = "accent", size = "default", onDone, testId, detail }: {
  reads: readonly ReadTarget[]; call: (reason: string) => Promise<unknown>; label: string; confirm?: string; ask?: string; tone?: Tone; size?: BtnSize;
  onDone?: (out: unknown) => void; testId?: string;
  /** Shown in the open dialog only, so it can fetch what the action touches. */
  detail?: ReactNode;
}) {
  const { busy, msg, run } = useRun(reads);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const formId = useId();
  async function execute() {
    let succeeded = false;
    const out = await run(async () => {
      const value = await call(reason);
      succeeded = true;
      return value;
    });
    if (succeeded) {
      setOpen(false);
      setReason("");
      onDone?.(out);
    }
  }
  return (
    <span className={size === "menu" ? "flex w-full flex-col gap-2" : "inline-flex items-center gap-2"}>
      <button disabled={busy} aria-busy={busy || undefined} data-testid={testId} className={btn(tone, size)}
        onClick={() => { if (confirm || ask) setOpen(true); else void execute(); }}>{busy ? `${label}…` : label}</button>
      {!open && msg && !msg.ok && <Note msg={msg} />}
      {(confirm || ask) && <Dialog open={open} onClose={() => { if (!busy) setOpen(false); }} title={label}
        description={confirm} busy={busy} size="small"
        footer={<><button type="button" disabled={busy} className={btn("plain", "form")} onClick={() => setOpen(false)}>Cancel</button>
          <button type="submit" form={formId} aria-busy={busy || undefined} disabled={busy || Boolean(ask && !reason.trim())} className={btn(tone, "form")}>{busy ? `${label}…` : label}</button></>}>
        <form id={formId} className="space-y-3" onSubmit={(event) => { event.preventDefault(); if (!busy && (!ask || reason.trim())) void execute(); }}>
          {open && detail}
          {ask && <label className="flex flex-col gap-2 text-sm text-ink">{ask}
            <textarea autoFocus required rows={3} value={reason} onChange={(event) => setReason(event.target.value)} disabled={busy} />
          </label>}
          {msg && !msg.ok && <p role="alert" className="text-sm text-danger">{msg.text}{msg.reauth && <> <ReauthLink /></>}</p>}
        </form>
      </Dialog>}
    </span>
  );
}

/** `width` sizes the control: `sm` for short codes and numbers, `md` (default) for names, `lg` for descriptions. */
export type Field = { name: string; label: string; type?: "text" | "number" | "select" | "textarea" | "checkbox";
  options?: (string | { value: string; label: string })[]; placeholder?: string; required?: boolean; initial?: string; help?: string; width?: "sm" | "md" | "lg" };
const FIELD_WIDTH = { sm: "w-32 max-w-full", md: "w-56 max-w-full", lg: "w-[28rem] max-w-full" } as const;

/** A form's values: text, a number for `number` fields, a boolean for checkboxes; an empty field is absent. */
export type Values = Partial<Record<string, string | number | boolean>>;

export const text = (x: Values[string]) => (x === undefined ? undefined : String(x));
export const numeric = (x: Values[string]) => (x === undefined ? undefined : Number(x));

/** A small form: `submit` turns its values into a request; `onDone` and `goto` get the typed result. */
export function Form<R>({ reads, fields, submit, label, onDone, goto, testId, children }: {
  reads: readonly ReadTarget[]; fields: Field[]; submit: (values: Values) => Promise<R>; label: string;
  onDone?: (out: R) => void; goto?: (out: R) => string; testId?: string; children?: ReactNode;
}) {
  const { busy, msg, run } = useRun(reads);
  const navigate = useNavigate();
  return (
    <form className="celia-form flex min-w-0 max-w-full flex-wrap items-end gap-3" data-testid={testId} onSubmit={async (ev) => {
      ev.preventDefault();
      const fd = new FormData(ev.currentTarget);
      const values: Values = {};
      for (const f of fields) {
        const v = fd.get(f.name);
        if (f.type === "checkbox") values[f.name] = v === "on";
        else if (typeof v === "string" && v !== "") values[f.name] = f.type === "number" ? Number(v) : v;
      }
      const out = await run(() => submit(values));
      if (out === undefined) return;
      onDone?.(out);
      if (goto) navigate(goto(out));
    }}>
      {fields.map((f) => (
        <label key={f.name} className={`celia-form-field flex min-w-0 max-w-full gap-1.5 ${f.type === "checkbox" ? "h-8 flex-row items-center" : "flex-col"}`}>
          <FieldLabel>{f.label}{f.help && <HelpTooltip label={f.label}>{f.help}</HelpTooltip>}</FieldLabel>
          {f.type === "select" ? (
            <FormSelect name={f.name} defaultValue={f.initial} className={f.width ? FIELD_WIDTH[f.width] : "min-w-40"}>{f.options?.map((o) => <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>{typeof o === "string" ? o : o.label}</option>)}</FormSelect>
          ) : f.type === "textarea" ? (
            <textarea name={f.name} placeholder={f.placeholder} required={f.required} defaultValue={f.initial} rows={3} className={FIELD_WIDTH[f.width ?? "lg"]} />
          ) : f.type === "checkbox" ? (
            <input type="checkbox" name={f.name} />
          ) : (
            <input name={f.name} type={f.type ?? "text"} step={f.type === "number" ? "any" : undefined} placeholder={f.placeholder}
              required={f.required} defaultValue={f.initial} className={f.width ? FIELD_WIDTH[f.width] : "min-w-44"} />
          )}
        </label>
      ))}
      {children}
      <button disabled={busy} aria-busy={busy || undefined} className={`${BTN} ${TONE.accent}`}>{busy ? `${label}…` : label}</button>
      <Note msg={msg} />
    </form>
  );
}

/** A request body edited as JSON, for the few inputs that are documents (bindings, class maps). */
export function JsonForm({ reads, initial, submit, label, rows = 6, testId }: {
  reads: readonly ReadTarget[]; initial: unknown; submit: (doc: Record<string, unknown>) => Promise<unknown>; label: string; rows?: number; testId?: string;
}) {
  const { busy, msg, run } = useRun(reads);
  const [text, setText] = useState(JSON.stringify(initial, null, 2));
  return (
    <div className="space-y-2" data-testid={testId}>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={rows} className="mono w-full text-xs" />
      <div className="flex items-center gap-2">
        <button disabled={busy} aria-busy={busy || undefined} className={`${BTN} ${TONE.accent}`}
          onClick={() => run(() => submit(JSON.parse(text)))}>{busy ? `${label}…` : label}</button>
        <Note msg={msg} />
      </div>
    </div>
  );
}

/** Shown while a page re-reads its data because something is still moving. */
export function Live({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted" data-testid="live">
      <span aria-hidden className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />Live
    </span>
  );
}
